import React, {memo, useState, useTransition} from 'react';
import {ModuleTabularPage} from "../../components";
import {AdminModuleFieldsData, AdminModules, AdminReqUrls} from "../../config/AdminConfig";
import {ThemedButton, ThemedIconButton} from "../../../common/components";
import {AnimationType, FramerAnimator} from "../../../common/fusion-ui/animation/FramerAnimator";
import {useTheme} from "../../../common/ProjectThemeProvider";
import {useToast} from "../../../common/fusion-ui/provider/ToastProvider";
import {printError, printLog} from "../../../common/CommonUtils";
import {CssVariant, IconClass} from "../../../common/fusion-ui/config/FusionConfig";
import {
    checkNull,
    checkNullArr,
    checkNullStr,
    copyTextToClipboard,
    downloadExcelFile,
    getArrLen,
    getJsonValue,
    isBoolTrue,
    isJsonValueTrue,
    trim
} from "../../../common/fusion-ui/utils/ReactUtils";
import {ThemedDialog} from "../../../app";
import {FileDropZone, Flexbox, GridBox, HorDiv, Label, ScrollView} from "../../../common/fusion-ui";
import * as XLSX from 'xlsx';
import {sendAdminRequest} from "../../utils/AdminUtils";

const MembersPage = memo(() => {
    const TAG = 'MembersPage';

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();

    const animator = new FramerAnimator();
    const mf = AdminModuleFieldsData[AdminModules.Members];
    // const membersReqColumns = Object.values(mf).map(item => item.column);
    const membersReqColumns = [mf.companyId.column, mf.companyName.column, mf.fullName.column, mf.memberMobile1.column, mf.memberEmail1.column, mf.status.column, mf.memberPrefix.column, mf.firstName.column, mf.lastName.column, mf.username.column, mf.vokUserPassword.column, mf.loginPin.column, mf.memberUser.column, mf.memberDob.column, mf.gender.column, mf.memberEmail2.column, mf.workPhone1.column, mf.workPhone2.column, mf.memberMobile2.column, mf.homePhone.column, mf.memberSpeciality1.column, mf.memberSpeciality2.column, mf.memberImage.column, mf.countryId.column, mf.stateId.column, mf.cityId.column, mf.timezoneId.column, mf.memberOrganisation1.column, mf.memberDesignation1.column, mf.memberOrganisation2.column, mf.memberDesignation2.column, mf.highestEducation.column, mf.interestArea1.column, mf.interestArea2.column, mf.memberBiography.column, mf.workAddress1.column, mf.workAddress2.column, mf.homeAddress.column, mf.memberRemark.column, mf.assignGroupId.column];

    const Fields = {
        showUploadExcelDialog: 'showUploadExcelDialog',
        uploadExcelDialogMsg: 'uploadExcelDialogMsg',

        selectedExcelFileName: 'selectedExcelFileName',
        selectedExcelFile: 'selectedExcelFile',
        selectedExcelFileRows: 'selectedExcelFileRows',

        uploadExcelBtn_asLoading: 'uploadExcelBtn_asLoading',
        uploadExcelBtn_disable: 'uploadExcelBtn_disable',
    };
    const [fieldsData, setFieldsData] = useState({});
    const [copyColsPending, startCopyColsTransition] = useTransition();
    const [downloadColsPending, startDownloadColsTransition] = useTransition();

    return (<>
        <ModuleTabularPage
            moduleName={AdminModules.Members}
            fetchRecordsUrl={AdminReqUrls.admin__member__mainPage_fetch_recordList}
            headerComponent={animator.setAnimationType(AnimationType.FADE_IN).setDelay(100).setDuration(300).render(renderHeaderComponent())}
            requestOptions={{fetchImages: true}}
        />
        {renderExcelDialog()}
    </>);

    function renderHeaderComponent() {
        return <div className={'w-100 mb-3'}>
            {/*upload-excel-btn*/}
            <ThemedButton
                className={'w-auto px-3 fs-sm py-1 text-nowrap'}
                text={'Upload Excel File'}
                elasticActive={true}
                outlined={true}
                height={'28px'}
                bgColor={theme.primary05}
                onClick={() => {
                    updateField(Fields.showUploadExcelDialog, true);
                }}
            />
        </div>
    }

    function renderExcelDialog() {
        return <ThemedDialog
            open={isFieldTrue(Fields.showUploadExcelDialog)}
            title={'Upload Excel'}
            titleAt={'start'}
            subtitle={`To upload member records, prepare an Excel file with the columns below. Drag and drop your file into the drop-box below or click on it to select your file. And click on upload button store the data directly in the database. You'll receive a confirmation upon completion (Refresh required upon completion).`}
            subtitleClassName={'fs-md'}
            subtitleStyle={{color: theme.text80}}
            widthClass={'col-sm-12 col-md-7'}
            maxHeight={'75vh'}
            closeOnOutsideClick={false}
            showCancelControl={true}
            onHide={handleHideExcelDialog}
            onClose={handleHideExcelDialog}
            showFooter={false}
        >
            {renderExcelPanel()}
        </ThemedDialog>
    }

    function renderExcelPanel() {
        return <div className={'px-2 mt-n1 mb-2'}>
            <div className={'px-1'}>
                {/*req-columns list*/}
                <GridBox className={''} md={6}>
                    <div className={'border-0-5 border-solid rounded-3 py-2'} style={{borderColor: theme.text20}}>
                    <span className={'px-2 fw-semi-bold select-none text-theme-resp'}>
                            Required Columns ({membersReqColumns.length}):&nbsp;
                        </span>
                        <ScrollView
                            className={`w-100 px-0`}
                            height={'30vh'}
                            scrollVariant={'dark'}
                            scrollVert={true}>
                            <table className={'text-theme-resp w-100'}>
                                {membersReqColumns.map((item, index) => {
                                    return <tr key={index + item} className={''}>
                                        <td className={'px-2 text-nowrap m-0 p-0 border-bottom-1 border-bottom-solid'}
                                            style={{borderBottomColor: theme.text50}}>{item}
                                        </td>
                                    </tr>
                                })}
                            </table>
                        </ScrollView>

                        <HorDiv variant={theme.text} width={'1.5px'}/>

                        {/*actions*/}
                        <Flexbox className={'mt-2 px-2'}>
                            {/*Copy cols btn*/}
                            <ThemedIconButton
                                className={'fs-sm'}
                                outlined={true}
                                circular={false}
                                tooltip={"Copy columns"}
                                size={'26px'}
                                icon={IconClass.copy}
                                asLoading={copyColsPending}
                                disabled={copyColsPending}
                                borderWidth={1}
                                onClick={() => {
                                    startCopyColsTransition(() => {
                                        const colStr = membersReqColumns.join(',');
                                        copyTextToClipboard(colStr);
                                        toast('Copied columns to clipboard.', CssVariant.info);
                                    });
                                }}
                            />

                            {/*dl cols btn*/}
                            <ThemedIconButton
                                className={'fs-sm'}
                                ml={3}
                                outlined={true}
                                circular={false}
                                tooltip={"Export columns as Excel"}
                                size={'26px'}
                                icon={IconClass.download}
                                asLoading={downloadColsPending}
                                disabled={downloadColsPending}
                                borderWidth={1}
                                onClick={() => {
                                    startDownloadColsTransition(() => {
                                        try {
                                            downloadExcelFile({
                                                fileName: `Members Excel File Columns.xlsx`, // columns: ['Required Columns'],
                                                // rows: membersReqColumns.map(item => [item]),
                                                columns: membersReqColumns.map(item => [item]), rows: membersReqColumns.map(item => [null])
                                            });
                                            toast('Exported columns');
                                        } catch (error) {
                                            toast('Export failed, please try again!', CssVariant.danger);
                                            logErr(error);
                                        }
                                    });
                                }}
                            />
                        </Flexbox>
                    </div>

                    {/*drop-box*/}
                    <div className={'h-100 overflow-hidden'}>
                        <FileDropZone
                            className={'w-100 h-100 highlight-info-hover highlight-border-info-hover'}
                            allowedExtensions={['.xls', '.xlsx']}
                            label="Drag & drop your excel file here, or click to select one"
                            bgColor={theme.t}
                            borderColor={theme.text10}
                            labelColor={theme.text40}
                            fileNameLabelColor={theme.text}
                            hoverBgColor={theme.primaryBlue30}
                            hoverBorderColor={theme.primaryBlue}
                            onFileSelect={(e, file, name, ext) => {
                                log('FileDropZone.onFileSelect.file:', file, 'name:', name, 'ext:', ext);
                                if (checkNull(file) && checkNullStr(name)) {
                                    // toast('File selected!', CssVariant.info);
                                    updateField(Fields.selectedExcelFileName, name + '');
                                    updateField(Fields.selectedExcelFile, file);
                                } else {
                                    // toast('No file selected!', CssVariant.warning);
                                    updateField(Fields.selectedExcelFileName, null);
                                    updateField(Fields.selectedExcelFile, null);
                                }
                                updateField(Fields.uploadExcelDialogMsg, '');
                            }}
                        />
                    </div>
                </GridBox>
            </div>

            {/*footer*/}
            <div className={'mt-2'}>
                {checkNullStr(getField(Fields.uploadExcelDialogMsg)) && <Label className={'bg-infox'} color={theme.text90} textAt={'start'}>
                    {getField(Fields.uploadExcelDialogMsg)}
                </Label>}

                <Flexbox className={'mt-2'} justifyAt={'end'}>
                    <ThemedButton
                        className={'w-auto px-3 fs-md py-1 text-nowrap'}
                        mx={2}
                        text={'Cancel'}
                        height={'28px'}
                        outlined={true}
                        variant={theme.text}
                        hoverBgColor={theme.text10}
                        hoverColor={theme.text}
                        hoverBorderColor={theme.text}
                        rippleColor={theme.text30}
                        onClick={handleHideExcelDialog}
                    />

                    <ThemedButton
                        className={'w-auto px-3 fs-md py-1 text-nowrap'}
                        ml={2}
                        text={'Upload'}
                        height={'28px'}
                        asLoading={isFieldTrue(Fields.uploadExcelBtn_asLoading)}
                        disabled={isFieldTrue(Fields.uploadExcelBtn_disable)}
                        onClick={handleUploadExcel}
                    />
                </Flexbox>
            </div>
        </div>
    }

    function handleUploadExcel() {
        const fun = 'handleUploadExcel:';
        try {
            validateSelectedExcelFile((dataToSend) => {
                log(fun, 'onSuccess.dataToSend:', dataToSend);
                // toast("File processed successfully, uploading now...");
                const verified = true;
                // const verified = false;
                if (verified) {
                    updateField(Fields.uploadExcelBtn_asLoading, true);
                    updateField(Fields.uploadExcelBtn_disable, true);

                    const REQUEST_OPTIONS = {
                        formData: dataToSend,
                    };

                    sendAdminRequest({
                        reqUrl: AdminReqUrls.admin__member__mainPage_uploadExcelFile,
                        reqOptions: REQUEST_OPTIONS,
                        onFetched: (fetchedData) => {
                            log(fun, 'fetchedData:', fetchedData);

                            const message = getJsonValue(fetchedData, 'message');
                            const success = isJsonValueTrue(fetchedData, 'success');

                            if (success) {
                                toast(message, CssVariant.success);
                                handleHideExcelDialog();
                            } else {
                                toast(message, CssVariant.danger);
                            }
                            updateField(Fields.uploadExcelBtn_asLoading, false);
                            updateField(Fields.uploadExcelBtn_disable, false);
                        }, onError: (error) => {
                            performFailureTasks(error);
                        }
                    });
                } else {
                    toast("Action disabled!", CssVariant.info);
                    updateField(Fields.uploadExcelBtn_asLoading, false);
                    updateField(Fields.uploadExcelBtn_disable, false);
                }
            });
        } catch (error) {
            performFailureTasks(error);
        }

        function performFailureTasks(error, msg = 'Upload failed, please try again') {
            if (checkNullStr(msg)) toast(msg, CssVariant.danger);
            updateField(Fields.uploadExcelBtn_asLoading, false);
            updateField(Fields.uploadExcelBtn_disable, false);
            if (error) logErr(fun, error);
        }
    }

    function validateSelectedExcelFile(onSuccess) {
        const fun = 'validateSelectedExcelFile:';
        let verified = false;
        updateField(Fields.uploadExcelDialogMsg, '');

        try {
            const file = getField(Fields.selectedExcelFile);
            if (checkNullStr(file)) {
                const reader = new FileReader();
                log(fun, 'file:', file);

                reader.onload = (event) => {
                    const binaryStr = event.target.result;
                    const workbook = XLSX.read(binaryStr, {type: 'binary'});
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(sheet, {header: 1});
                    // setData(jsonData);
                    log(fun, 'onload.jsonData:', jsonData);

                    //Extract columns & rows
                    if (checkNullArr(jsonData)) {
                        //Extract rows
                        // const excelDataFormat = [
                        //     ["Column 1", "Column 2", "Column 3", "Column 4"],
                        //     ["Cell: Row1-Column1"],
                        //     ["Cell: Row2-Column1", "Cell: Row2-Column2", "Cell: Row2-Column3", null]
                        // ];

                        // Extract column names
                        let excelColumns = jsonData.shift();
                        if (checkNullArr(excelColumns)) {
                            excelColumns = excelColumns.map(item => trim(item));
                            log(fun, 'onload.jsonData.excelColumns:', excelColumns);

                            //Check if any columns are missing
                            const missingColumns = membersReqColumns.filter(column => !excelColumns.includes(column));
                            log(fun, 'onload.jsonData.missingColumns:', missingColumns);
                            if (!checkNullArr(missingColumns)) {
                                // Process rows
                                const excelRows = jsonData.map(row => {
                                    // Fill missing cells with empty strings or null if explicitly provided
                                    return excelColumns.map((_, index) => checkNullStr(row[index]) ? trim(row[index]) : "");
                                });
                                log(fun, 'onload.jsonData.excelRows:', excelRows);
                                if (checkNullArr(excelRows)) {
                                    //Convert excel-rows into json-rows for DB: [ { "Column1:" "Cell1" }, { "Column2:" "Cell2"}, ...rest ];
                                    const jsonRows = excelRows.map(row => {
                                        return excelColumns.reduce((acc, col, index) => {
                                            acc[col] = checkNullStr(row[index]) ? row[index] : "";
                                            return acc;
                                        }, {});
                                    });
                                    log(fun, 'onload.jsonData.jsonRows:', jsonRows);
                                    verified = true;
                                    if (onSuccess) onSuccess({columns: excelColumns, rows: jsonRows, rowsOrg: excelRows, excelData: jsonData});
                                } else {
                                    toast("No rows found in the excel-file!", CssVariant.danger);
                                }
                            } else {
                                toast("Some columns are missing!", CssVariant.danger);
                                updateField(Fields.uploadExcelDialogMsg, <span>
                                <span className={'fw-bold text-danger'}>These columns are missing ({getArrLen(missingColumns)}): </span>
                                    {missingColumns.join(', ')}
                            </span>);
                            }
                        } else {
                            toast("Columns not found in the excel-file!", CssVariant.danger);
                        }
                    } else {
                        toast("No data found in the excel-file!", CssVariant.danger);
                    }
                };
                reader.readAsBinaryString(file);
            } else {
                toast("No excel file selected!", CssVariant.danger);
            }
        } catch (error) {
            toast("Couldn't read excel data, please try again!", CssVariant.danger);
            logErr(fun, error);
        }
        return verified;
    }

    function handleHideExcelDialog() {
        updateField(Fields.showUploadExcelDialog, false);
        updateField(Fields.uploadExcelDialogMsg, null);
        updateField(Fields.selectedExcelFileName, null);
        updateField(Fields.selectedExcelFile, null);
        updateField(Fields.selectedExcelFileRows, null);
        updateField(Fields.uploadExcelBtn_asLoading, false);
        updateField(Fields.uploadExcelBtn_disable, false);
    }


    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default MembersPage;
